import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, Skeleton } from '@clari/ui'

/**
 * Screen-area loading placeholders. These mirror the footprint of the real
 * content they stand in for, so swapping skeleton → data causes no layout
 * shift (CLS). Kept local to apps/web: they trace app-specific layouts, not
 * reusable primitives — the generic <Skeleton> lives in @clari/ui.
 *
 * A11y: each screen area exposes ONE polite live region with a localized
 * "loading" label; the visual placeholders are hidden from assistive tech
 * (they carry no meaning). This avoids the many-status-regions noise you'd get
 * from the bare <Skeleton> primitive (itself role=status) repeated N times.
 */
function LoadingRegion({ children, className }: { children: ReactNode; className?: string }) {
  const { t } = useTranslation('banking')
  return (
    <div role="status" aria-live="polite">
      <span className="sr-only">{t('common.loading')}</span>
      <div aria-hidden="true" className={className}>
        {children}
      </div>
    </div>
  )
}

/** One transaction row — mirrors TransactionItem (py-3, name + type, amount). */
function TxRow() {
  return (
    <li className="flex items-center justify-between gap-3 border-b border-border py-3 last:border-0">
      <span className="flex min-w-0 flex-col gap-2">
        <Skeleton width={160} height={16} />
        <Skeleton width={90} height={12} />
      </span>
      <Skeleton width={72} height={16} />
    </li>
  )
}

function TxRows({ rows }: { rows: number }) {
  return (
    <ul className="flex flex-col">
      {Array.from({ length: rows }, (_, i) => (
        <TxRow key={i} />
      ))}
    </ul>
  )
}

/** Standalone transaction list placeholder (Transactions page). */
export function TransactionListSkeleton({ rows = 8 }: { rows?: number }) {
  return (
    <LoadingRegion>
      <TxRows rows={rows} />
    </LoadingRegion>
  )
}

/** Balance card — mirrors the Dashboard balance Card (label + amount + badge, actions). */
function BalanceCardInner() {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton width={120} height={14} />
          <Skeleton width={150} height={30} />
        </div>
        <Skeleton width={84} height={28} />
      </div>
      <div className="mt-4 flex gap-2">
        <Skeleton width={132} height={44} />
        <Skeleton width={96} height={44} />
      </div>
    </Card>
  )
}

/** Recent-transactions card — mirrors the Dashboard recent Card (header + list). */
function RecentCardInner({ rows = 5 }: { rows?: number }) {
  return (
    <Card>
      <div className="mb-2 flex items-center justify-between">
        <Skeleton width={160} height={20} />
        <Skeleton width={60} height={14} />
      </div>
      <TxRows rows={rows} />
    </Card>
  )
}

/** Dashboard account area (balance + recent transactions) in one live region. */
export function DashboardAccountSkeleton() {
  return (
    <LoadingRegion className="flex flex-col gap-4">
      <BalanceCardInner />
      <RecentCardInner rows={5} />
    </LoadingRegion>
  )
}

/** Account card — mirrors the /account IBAN Card (balance, IBAN/BIC/status, actions). */
function AccountCardInner() {
  return (
    <Card>
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <Skeleton width={120} height={14} />
          <Skeleton width={150} height={30} />
        </div>
        <Skeleton width={72} height={24} />
      </div>
      <div className="mt-4 grid grid-cols-3 gap-y-2">
        <Skeleton width={60} height={14} />
        <div className="col-span-2">
          <Skeleton width="80%" height={14} />
        </div>
        <Skeleton width={60} height={14} />
        <div className="col-span-2">
          <Skeleton width={140} height={14} />
        </div>
        <Skeleton width={60} height={14} />
        <div className="col-span-2">
          <Skeleton width={90} height={14} />
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <Skeleton width={120} height={44} />
        <Skeleton width={110} height={44} />
      </div>
    </Card>
  )
}

/** Account page body while the accounts query loads. */
export function AccountSkeleton() {
  return (
    <LoadingRegion className="flex flex-col gap-4">
      <AccountCardInner />
    </LoadingRegion>
  )
}
